import { ScrollArea } from "@base-ui/react/scroll-area"
import React from "react"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { ProcessStep, ProcessStepState } from "shared/types/ProcessStep"
import { WithClassName } from "shared/types/WithClassName"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import {
  getProcessStepPosition,
  resolveProcessStepState
} from "./lib/resolveProcessStepState"

interface ProcessStepsProps<T extends string> extends WithClassName {
  steps: ProcessStep<T>[] | null
  activeValue: T | null
}

function getStepModifierClassName(stepState: ProcessStepState) {
  return `process-step--${stepState.toLowerCase()}`
}

function getMarkerIcon(state: ProcessStepState, icon: IconName) {
  switch (state) {
    case ProcessStepState.DONE:
      return IconName.CHECK
    case ProcessStepState.FAILED:
    case ProcessStepState.SKIPPED:
      return IconName.CLOSE
    default:
      return icon
  }
}

function ProcessStepsComponent<T extends string>({
  steps,
  activeValue,
  className
}: ProcessStepsProps<T>) {
  const allSteps = steps ?? []
  const activeIndex = allSteps.findIndex(step => step.value === activeValue)
  const stepEntries = allSteps.map((step, index) => ({
    step,
    state: resolveProcessStepState(
      getProcessStepPosition(index, activeIndex),
      step.status
    )
  }))

  return (
    <ScrollArea.Root
      className={classNames(
        "process-steps",
        "card",
        "card--elevated",
        "scroll-area",
        "scroll-area--horizontal",
        "shadow-s",
        className
      )}
    >
      <ScrollArea.Viewport className="scroll-area__viewport">
        {/* ScrollArea.Content defaults to role="presentation", which would strip the
            <ol> out of the accessibility tree, so the list role is restored here. */}
        <ScrollArea.Content
          render={<ol />}
          role="list"
          className="scroll-area__content process-steps__list"
        >
          {stepEntries.map(({ step, state }, index) => {
            const previousEntry = index > 0 ? stepEntries[index - 1] : null
            const isConnectorComplete =
              previousEntry?.state === ProcessStepState.DONE

            return (
              <li
                key={step.value}
                className={classNames(
                  "process-step",
                  getStepModifierClassName(state)
                )}
                aria-current={
                  state === ProcessStepState.CURRENT ? "step" : undefined
                }
              >
                {index > 0 && (
                  <span
                    className={classNames("process-steps__connector", {
                      "process-steps__connector--complete": isConnectorComplete
                    })}
                    aria-hidden="true"
                  />
                )}
                <span className="process-step__content">
                  <span className="process-step__marker">
                    <Icon
                      name={getMarkerIcon(state, step.icon)}
                      size={IconSize.MEDIUM}
                    />
                  </span>
                  <span className="process-step__text">
                    <span className="process-step__label">{step.label}</span>
                    <span className="process-step__description">
                      {step.description}
                    </span>
                  </span>
                </span>
              </li>
            )
          })}
        </ScrollArea.Content>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        orientation="horizontal"
        className="scroll-area__scrollbar"
      >
        <ScrollArea.Thumb className="scroll-area__thumb" />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>
  )
}

export const ProcessSteps = React.memo(
  ProcessStepsComponent
) as typeof ProcessStepsComponent
